import { useEffect, useRef } from 'react';
import { keepPreviousData, useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { createAuthenticatedWebSocket } from '../lib/websocket.ts';
import { useAuthStore } from '../stores/auth-store.ts';
import {
  listNotifications,
  getUnreadCount,
  markAsRead,
  markAllAsRead,
  deleteNotification,
  getNotificationPreferences,
  updateNotificationPreferences,
} from '../api/endpoints/notifications.ts';
import type { NotificationPreferences } from '../api/types.ts';
import { STALE_TIMES, GC_TIMES } from '../lib/constants.ts';

export const notificationQueryKeys = {
  all: ['notifications'] as const,
  list: (params?: { unreadOnly?: boolean }) => ['notifications', 'list', params] as const,
  unreadCount: ['notifications', 'unread-count'] as const,
  preferences: ['notifications', 'preferences'] as const,
};

export function useNotifications(params?: {
  page?: number;
  pageSize?: number;
  unreadOnly?: boolean;
}) {
  return useQuery({
    queryKey: notificationQueryKeys.list(params),
    queryFn: () => listNotifications(params),
    staleTime: STALE_TIMES.notifications,
    gcTime: GC_TIMES.notifications,
    placeholderData: keepPreviousData,
  });
}

export function useUnreadCount() {
  const live = useNotificationInboxInvalidation();
  return useQuery({
    queryKey: notificationQueryKeys.unreadCount,
    queryFn: () => getUnreadCount(),
    staleTime: STALE_TIMES.notifications,
    gcTime: GC_TIMES.notifications,
    refetchInterval: live ? false : 60_000,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: true,
  });
}

function useNotificationInboxInvalidation() {
  const queryClient = useQueryClient();
  const accessToken = useAuthStore((s) => s.accessToken);
  const liveRef = useRef(false);

  useEffect(() => {
    if (!accessToken) return;
    const ws = createAuthenticatedWebSocket(accessToken);
    const reconnect = { timer: undefined as ReturnType<typeof setTimeout> | undefined };

    ws.onopen = () => {
      liveRef.current = true;
      ws.send(JSON.stringify({ event: 'join', data: { room: 'notifications:me' } }));
    };
    ws.onmessage = (event) => {
      try {
        const msg = JSON.parse(event.data);
        if (msg.event === 'notification:created') {
          queryClient.invalidateQueries({ queryKey: notificationQueryKeys.all });
        }
      } catch {
        /* ignore */
      }
    };
    ws.onclose = () => {
      liveRef.current = false;
    };

    return () => {
      clearTimeout(reconnect.timer);
      if (ws.readyState === WebSocket.OPEN) {
        ws.send(JSON.stringify({ event: 'leave', data: { room: 'notifications:me' } }));
      }
      ws.close();
    };
  }, [accessToken, queryClient]);

  return liveRef.current;
}

export function useMarkAsRead() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (notificationId: string) => markAsRead(notificationId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: notificationQueryKeys.all });
    },
  });
}

export function useMarkAllAsRead() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => markAllAsRead(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: notificationQueryKeys.all });
    },
  });
}

export function useDeleteNotification() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (notificationId: string) => deleteNotification(notificationId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: notificationQueryKeys.all });
    },
  });
}

export function useNotificationPreferences() {
  return useQuery({
    queryKey: notificationQueryKeys.preferences,
    queryFn: () => getNotificationPreferences(),
    staleTime: STALE_TIMES.notifications,
    gcTime: GC_TIMES.notifications,
  });
}

export function useUpdateNotificationPreferences() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (preferences: NotificationPreferences) =>
      updateNotificationPreferences(preferences),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: notificationQueryKeys.preferences });
    },
  });
}
